/**
 * V70-C — 게임대회 리더보드 고정 10행 표시 정책 (게임센터 미니 리더보드 · 전체 리더보드 페이지 공통)
 *   - 1~10위 행을 항상 표시한다. 유효 기록이 없는 순위는 참가자·점수를 "—" 로 둔다.
 *   - 유효 기록만 점수 내림차순으로 다시 정렬해 1위부터 채운다 (기록 3건 → 1~3위만 채움).
 *   - 동점은 서버가 내려준 순서(서버 tie-break: 타자왕 lastPlayedAt, 마블 playCount 등)를 유지하고
 *     순위는 서버와 같이 1, 2, 3… 연속 번호로 매긴다.
 *   - 공식 기록 출처(서버 모드)가 아니면 어떤 행도 순위에 넣지 않는다 (demo·mock·localStorage 기록 제외).
 *   - demo/mock/sample 식별자, 빈 UID, 숫자가 아닌 점수는 유효 기록이 아니다. 같은 UID 는 첫 행만 사용.
 */
export const LEADERBOARD_SLOT_COUNT = 10;
export const LEADERBOARD_EMPTY_CELL = '—';

export interface LeaderboardSlot<T> {
  rank: number;
  /** 유효 기록이 없는 순위는 null */
  row: T | null;
}

const NON_OFFICIAL_UID = /^(demo|mock|sample|local)[-_:]/i;

export function isValidLeaderboardRecord(uid: unknown, score: unknown): boolean {
  if (typeof uid !== 'string' || uid.trim() === '' || NON_OFFICIAL_UID.test(uid)) return false;
  return typeof score === 'number' && Number.isFinite(score);
}

export function buildLeaderboardSlots<T>(
  rows: readonly T[] | null | undefined,
  getUid: (row: T) => string,
  getScore: (row: T) => number,
  officialSource: boolean,
): LeaderboardSlot<T>[] {
  const seen = new Set<string>();
  const valid = (officialSource ? rows ?? [] : [])
    .filter((r) => isValidLeaderboardRecord(getUid(r), getScore(r)))
    .filter((r) => (seen.has(getUid(r)) ? false : (seen.add(getUid(r)), true)))
    .map((r, i) => ({ r, i }))
    .sort((a, b) => getScore(b.r) - getScore(a.r) || a.i - b.i)
    .map((x) => x.r);
  return Array.from({ length: LEADERBOARD_SLOT_COUNT }, (_, i) => ({ rank: i + 1, row: valid[i] ?? null }));
}
